import axios from 'axios'
import * as _ from 'lodash'
import { notify } from '@/notifications'
import Mission from '@/models/Mission'

const UPDATE_MISSION = 'UPDATE_MISSION'
const CLEAR_MISSION = 'CLEAR_MISSION'
const SET_MAP_SETTINGS = 'SET_MAP_SETTINGS'
const ADD_NOTIFICATION = 'ADD_NOTIFICATION'
const SET_UPSELL_PAGE_DISPLAY = 'SET_UPSELL_PAGE_DISPLAY'

let missionSettingsQueue = []
let missionSettingsTimeout = null

export default {
    namespaced: true,

    state: {
        mission: new Mission(),
        mapSettings: JSON.parse(localStorage.getItem('mapSettings')) || {
            viewCallsignLabels: true,
            receiveStaleUserAlerts: true,
            clusterOverlappingPlis: true,
            callsign: null,
        },
        dismissedNotifications: JSON.parse(localStorage.getItem('dismissedNotifications')) || {},
        displayUpsellPage: false,
    },

    getters: {
        mission(state) {
            return state.mission
        },

        mapSettings(state) {
            return state.mapSettings
        },

        hasDismissedMessage(state) {
            return (notification) => state.dismissedNotifications[notification] || false
        },

        baseUrl(state, getters, rootState) {
            return `/${rootState.auth.organization.slug}/missions`
        },
    },

    mutations: {
        [UPDATE_MISSION](state, { mission }) {
            state.mission.set(mission)
        },

        [CLEAR_MISSION](state) {
            state.mission = new Mission()
        },

        [SET_MAP_SETTINGS](state, settings) {
            state.mapSettings = settings
        },

        [ADD_NOTIFICATION](state, notification) {
            state.dismissedNotifications[notification] = true
            localStorage.setItem('dismissedNotifications', JSON.stringify(state.dismissedNotifications))
        },

        [SET_UPSELL_PAGE_DISPLAY](state, display) {
            state.displayUpsellPage = display
        },
    },

    actions: {
        // Temporary hack to get default mission (only 1 mission per org for now)
        async getDefaultMission({ commit, getters }) {
            await axios.get(getters.baseUrl).then(async ({ data }) => {
                const defaultMission = _.find(data, (m) => m.name === 'default mission')
                commit(UPDATE_MISSION, { mission: defaultMission })
            })
        },

        clearMission({ commit }) {
            commit(CLEAR_MISSION)
        },

        getMapData({ commit, state, getters }) {
            return axios.get(`${getters.baseUrl}/${state.mission.get('id')}/map-data`).then(({ data }) => {
                state.mission.updateFromData(data)
                commit(UPDATE_MISSION, { mission: state.mission })
            })
        },

        getNetworks({ state, getters }) {
            return axios.get(`${getters.baseUrl}/${state.mission.get('id')}/networks`).then(({ data }) => data)
        },

        updateMissionSettingsQueued({ getters }, payload) {
            missionSettingsQueue.push({
                key: payload.key,
                [payload.property]: payload.value,
            })
            if (missionSettingsTimeout) {
                clearTimeout(missionSettingsTimeout)
            }
            missionSettingsTimeout = setTimeout(() => {
                axios
                    .patch(`${getters.baseUrl}/${payload.missionId}/user/settings`, {
                        settings_queue: missionSettingsQueue,
                    })
                    .then(() => {
                        missionSettingsQueue = []
                    })
                    .catch(() => {
                        notify('There was a problem saving your map settings.')
                    })
                    .finally(() => {
                        clearTimeout(missionSettingsTimeout)
                    })
            }, 2000)
        },

        updateVisibilitySetting({ state, getters, dispatch }, { visible, userLocations }) {
            const settings = userLocations.map((u) => {
                const id = u.get(u.identifier)
                return {
                    key: `${u.get('object_name')}_${id}`,
                    visible,
                }
            })

            return axios
                .patch(`${getters.baseUrl}/${state.mission.get('id')}/user/settings`, {
                    settings_queue: settings,
                })
                .finally(() => {
                    const gids = userLocations.map((u) => {
                        return u.get(u.identifier)
                    })

                    dispatch('refreshUserLocations', { gids })
                })
        },

        async getMapSettings({ commit }) {
            const request = await axios.get(`/user/map_settings`)
            const userMapSettings = request.data
            const settingsObj = {
                viewCallsignLabels: userMapSettings.view_callsign_labels,
                receiveStaleUserAlerts: userMapSettings.receive_stale_user_alerts,
                clusterOverlappingPlis: userMapSettings.cluster_overlapping_plis,
                callsign: userMapSettings.callsign,
            }

            commit(SET_MAP_SETTINGS, settingsObj)
        },

        saveMapSettings({ commit }, settings) {
            const requestSettings = Object.entries(settings).reduce((accum, setting) => {
                const [key, value] = setting
                const snakeCaseKey = _.snakeCase(key)
                accum[snakeCaseKey] = value
                return accum
            }, {})

            axios.put(`/user/map_settings`, requestSettings).then(() => {
                commit(SET_MAP_SETTINGS, settings)
            })
        },

        refreshUserLocations({ state, getters }, { gids }) {
            const gidStr = gids.join(',')
            const url = `${getters.baseUrl}/${state.mission.get('id')}/user-locations?gids=${gidStr}`

            return axios.get(url).then(({ data }) => {
                state.mission.updateFromData(data)
            })
        },

        addDismissedNotification({ commit }, notification) {
            commit(ADD_NOTIFICATION, notification)
        },

        setUpsellPageDisplay({ commit }, display) {
            commit(SET_UPSELL_PAGE_DISPLAY, display)
        },
    },
}
